import React from 'react';
import { Box } from '@material-ui/core';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';

import { find } from '../../feathersFunctionalWrapper';

export const getAccountOptions = async (organizationId, additionalParams = {}) => {
  const accountsQuery = {
    $and: [{ organizationId }, { inactive: { $or: [null, false] }, ...additionalParams }],
    $skip: 0,
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };

  let accounts = [];
  let accountsResult;
  do {
    accountsResult = await find('accounts', { query: accountsQuery });
    accounts = accounts.concat(accountsResult.data);
    accountsQuery.$skip += accountsQuery.$limit;
  } while (accountsResult.total > accounts.length);
  return accounts;
};

export const getVendorOptions = async (organizationId, addNew = false) => {
  const query = {
    organizationId,
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };
  const vendorsResult = await find('vendors', { query });
  if (addNew) {
    vendorsResult.data.unshift({
      name: '+ Add New',
      id: 'New Instance',
    });
  }
  return vendorsResult.data;
};

export const getPropertyOptions = async (organizationId, additionalParams = {}) => {
  const query = {
    organizationId,
    inactive: false,
    ...additionalParams,
    $limit: 500,
    $sort: {
      address1: 1,
      id: 1,
    },
  };
  const propertiesResult = await find('properties', { query });
  return propertiesResult.data;
};

export const getUnitOptions = async (organizationId, additionalParams = {}) => {
  const query = {
    organizationId,
    inactive: { $or: [null, false] },
    ...additionalParams,
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };
  const collator = new Intl.Collator('en', { numeric: true });
  const result = await find('units', { query });
  return result.data.sort((a, b) => collator.compare(a.name, b.name));
};

export const getSubportfolioOptions = async (organizationId, additionalParams = {}) => {
  const query = {
    organizationId,
    ...additionalParams,
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };
  const result = await find('entities', { query });
  return result.data;
};

export const getBalanceSheetAccountOptions = async (organizationId, additionalParams = {}) => {
  const accountsQuery = {
    organizationId,
    inactive: { $or: [null, false] },
    type: ['Asset', 'Liability', 'Equity'],
    ...additionalParams,
    $limit: 500,
    $skip: 0,
    $sort: {
      name: 1,
      id: 1,
    },
  };

  let accounts = [];
  let accountsResult;
  do {
    accountsResult = await find('accounts', { query: accountsQuery });
    accounts = accounts.concat(accountsResult.data);
    accountsQuery.$skip += accountsQuery.$limit;
  } while (accountsResult.total > accounts.length);
  return accounts;
};

export const getFixedAssetOptions = async (organizationId, addNew = false) => {
  const query = {
    organizationId,
    inactive: { $or: [null, false] },
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };
  const assetsResult = await find('fixed-assets', { query });
  if (addNew) {
    assetsResult.data.unshift({
      name: '+ Add New',
      id: 'New Instance',
    });
  }
  return assetsResult.data;
};

export const getPaymentAccountOptions = async (organizationId, additionalParams = {}) => {
  const query = {
    organizationId,
    inactive: { $or: [null, false] },
    type2: [
      'Bank',
      'Credit Card',
      'Property Management',
      'Owner Funds',
      'HELOC',
      'Accounts Payable',
      'Prepaid Expense',
    ],
    ...additionalParams,
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };
  const accountsResult = await find('accounts', { query });
  return accountsResult.data;
};

export const getImportAccountOptions = async (organizationId) => {
  const query = {
    organizationId,
    inactive: { $or: [null, false] },
    type2: ['Bank', 'Credit Card'],
    $limit: 500,
    $sort: {
      name: 1,
      id: 1,
    },
  };
  const accountsResult = await find('accounts', { query });

  const newArr = [{ name: 'All Accounts', id: 'All' }, ...accountsResult.data];
  return newArr;
};

export const subaccountSort = (accounts) => {
  const accountsObject = {};
  let currentAccount;

  // prep accounts for subaccount sort
  accounts.forEach((account) => {
    account.subAccounts = [];
    account.filterString = account.name;
    accountsObject[account.id] = account;
  });

  // build subaccounts
  accounts.forEach((account) => {
    if (account.parentAccountId) {
      if (accountsObject[account.parentAccountId]) {
        accountsObject[account.parentAccountId].subAccounts.push(account);
        accountsObject[account.parentAccountId].filterString += `\`${account.name}`;
        account.filterString += `\`${accountsObject[account.parentAccountId].name}`;
      } else {
        account.parentAccountId = null;
      }
    }
  });

  // eliminate all but parent accounts
  accounts = accounts.filter((account) => !account.parentAccountId);

  // add subaccounts into accounts array
  for (let i = 0; i < accounts.length; i += 1) {
    currentAccount = accounts[i];
    while (currentAccount.subAccounts.length) {
      i += 1;
      accounts.splice(i, 0, currentAccount.subAccounts.shift());
    }
  }

  return accounts;
};

export const filterSubaccounts = createFilterOptions({
  stringify: (option) => option.filterString,
});

export const padSubaccounts = (account) => {
  if (!account.parentAccountId) {
    return account.name;
  }
  return <Box marginLeft={2}>{account.name}</Box>;
};
